<template>
  <!-- v-if="!isEmpty($instantSearchProps)"
    v-bind="$instantSearchProps" -->
  <!-- Always mounted, never v-if-toggled on instantSearchProps. Vue's hasPropsChanged
    crashes on Object.keys(nextProps) when a v-if-guarded InstantSearch's next vnode
    props transiently resolve to null/undefined during a patch — this happened both on
    logout (SANITETA-APP-3/4) and on plain in-app navigation (breadcrumb clicks), since
    HeaderBreadcrumbs lives inside this subtree and route changes mutate GlobalStore
    state in the same flush as the router-view swap. Keeping the component permanently
    mounted removes the toggle entirely, rather than chasing each new trigger.
    The remaining APP-3/4 trigger was ExploreStore.searchParameters being set to null
    (PageBrand unmount) — ais-configure's v-bind below must always get an object. -->
  <!-- reduced-motion="user": motion components drop transform/layout animations when the OS
    prefers-reduced-motion is on (opacity/color still animate). Imperative animate() calls
    and CSS transitions are not covered by this — see SanTransitionSlide and reset.css. -->
  <MotionConfig reduced-motion="user">
    <InstantSearch v-bind="mountedInstantSearchProps">
      <ais-configure v-bind="searchParameters || {}" />
      <!-- router-view stays mounted under the overlay rather than v-else-swapped —
        isBlocked can flip mid-navigation (see initApi.js's 503 interceptor), and
        toggling this subtree hit the exact null-vnode unmount crash the InstantSearch
        comment above describes (SANITETA-APP-14, regressed by a84323f). -->
      <router-view />
      <MaintenanceScreen v-if="showMaintenanceScreen" />
    </InstantSearch>
  </MotionConfig>
</template>

<script setup>
defineOptions({ name: 'App' })

import { ref, computed, watch, inject, provide } from 'vue' // eslint-disable-line
import { useRoute } from 'vue-router'
import { useGlobalStore, useExploreStore, useAuthStore, useMaintenanceStore, useUserStore } from '@/stores'
import { storeToRefs } from 'pinia'
// import { useQuasar } from 'quasar'
import { isEmpty } from 'lodash' // eslint-disable-line
import { useEchoPublic } from '@laravel/echo-vue'
import { MotionConfig } from 'motion-v'
import InstantSearch from '@/components/instant-search/InstantSearch.vue'
import MaintenanceScreen from '@/components/maintenance/MaintenanceScreen.vue'

// const $q = useQuasar()
const { autoDark, instantSearchProps } = storeToRefs(useGlobalStore())
const { searchParameters } = storeToRefs(useExploreStore())
const { collection } = storeToRefs(useExploreStore())
useAuthStore().restoreSession()

const $queryClient = inject('$queryClient')
const $route = useRoute()
const { isBlocked } = storeToRefs(useMaintenanceStore())
const { setMaintenance } = useMaintenanceStore()

// Auth and legal routes stay reachable during maintenance — Super Admins still
// need the login page, and their bearer tokens bypass the 503.
const showMaintenanceScreen = computed(() => isBlocked.value && $route.matched.some(r => r.meta.authRequired))

// Public channel, no auth: fired on enable/schedule/edit/end (not at a scheduled start).
useEchoPublic('maintenance', '.maintenance.changed', setMaintenance)

// Queries that ran while blocked cached 503 failures (or empty fallbacks, e.g.
// roles on the auth pages), so mark everything stale to refetch on recovery.
watch(isBlocked, async (blocked, wasBlocked) => {
  if (wasBlocked && !blocked) {
    $queryClient.invalidateQueries()
    const userStore = useUserStore()
    if (!userStore.id) await userStore.fetchUser()
  }
})

// Pages that use search (Brands, Explore, …) set GlobalStore.instantSearchProps
// themselves. Off those pages it's null — indexName is a required prop on
// InstantSearch, and TypesenseInstantsearchAdapter's constructor throws
// synchronously if additionalSearchParameters has no query_by/preset, so both
// need a fallback here even though no widgets are mounted against this instance
// to ever issue a real search with it.
const mountedInstantSearchProps = computed(() => instantSearchProps.value ?? {
  indexName: collection.value,
  searchParams: { query_by: 'title' }
})

// const $instantSearchProps = ref()
// const $isInstantsearch = computed(() => !isEmpty($instantSearchProps.value))

// const sanIcons = {
//   arrow_upward: 'mono-arrow-up'
// }
// $q.iconMapFn = cls => {
//   cls = sanIcons[cls] || cls
//   if (cls.startsWith('flag-')) cls = `san-flag fi fis fi-${cls.replace(/^flag-/, '')}`
//   return { cls }
// }
// $q.iconMapFn = icon => {
//   icon = sanIcons[icon] || icon
//   return { icon }
// }

// Set isDark from system
autoDark.value = window.matchMedia('(prefers-color-scheme: dark)').matches

// Autodetect system theme changes
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', ({ matches }) => {
  autoDark.value = matches
})

watch(() => searchParameters.value, val => {
  // console.log('searchParameters', val)
})

// provide('$instantSearchProps', $instantSearchProps)
// provide('$isInstantsearch', $isInstantsearch)
</script>
